import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { derivedFrom } from 'ngxtension/derived-from';
import { debounceTime, map, pipe } from 'rxjs';

import type { DropdownOption } from '@valk-nx/components/ui-select-shell/src/lib/select-shell.interface';
import type { IsoCountryCode } from '@valk-nx/services/location-search/src/lib/location-search.interface';
import {
	LocationSearchService,
	MINIMUM_QUERY_LENGTH,
} from '@valk-nx/services/location-search/src/lib/location-search.service';
import { FilterableListService } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';

export const LOCATION_SEARCH_DEBOUNCE_MS = 300;

// TODO: VPL-49088 - Make the list of countries configurable or fetch from an API
export const LOCATION_SEARCH_COUNTRIES: IsoCountryCode[] = [
	'nl',
	'de',
	'be',
	'fr',
	'es',
	'cw',
];

/**
 * Glue between the location search box and the list request: debounces the
 * typed term into the Mapbox suggest query and feeds the coordinates of a
 * picked suggestion into the BFF call. Provide on the page component so its
 * lifetime matches the page.
 */
@Injectable()
export class LocationSearchFilter {
	private readonly locationSearchService = inject(LocationSearchService);
	private readonly filterableListService = inject(FilterableListService);

	private readonly term = signal('');
	private readonly debouncedTerm = derivedFrom(
		[this.term],
		pipe(
			debounceTime(LOCATION_SEARCH_DEBOUNCE_MS),
			map(([term]) => term),
		),
		{ initialValue: '' },
	);

	readonly options = computed<DropdownOption[]>(() =>
		this.locationSearchService.suggestions().map((suggestion) => ({
			value: suggestion.mapbox_id,
			label: suggestion.name,
			selectLabel: suggestion.place_formatted
				? `${suggestion.name}, ${suggestion.place_formatted}`
				: suggestion.name,
		})),
	);

	// Loading covers the debounce window (typed term not yet committed as the
	// query) as well as the in-flight request, so the panel never shows
	// "no results" for a search that is still on its way
	readonly loading = computed<boolean>(() => {
		const term = this.term();
		if (term.trim().length < MINIMUM_QUERY_LENGTH) {
			return false;
		}

		return (
			this.locationSearchService.query() !== term ||
			this.locationSearchService.suggestionsResource.isLoading()
		);
	});

	constructor() {
		this.locationSearchService.countries.set(LOCATION_SEARCH_COUNTRIES);

		effect(() => {
			this.locationSearchService.query.set(this.debouncedTerm());
		});

		effect(() => {
			this.locationSearchService.language.set(
				this.filterableListService.language(),
			);
		});
	}

	onSearched(term: string): void {
		this.term.set(term);

		if (!term) {
			this.filterableListService.location.set(null);
		}
	}

	onSelected(mapboxId: string): void {
		const suggestion = this.locationSearchService
			.suggestions()
			.find((option) => option.mapbox_id === mapboxId);

		this.filterableListService.location.set(
			suggestion?.coordinates ?? null,
		);

		this.term.set('');
	}
}
